import React, { useState, useEffect } from 'react';
import { getGames, createGame, updateGame, deleteGame } from '../../services/api';
import toast from 'react-hot-toast';

const GENRES = ['Action', 'RPG', 'Strategy', 'Sports', 'Puzzle', 'Racing', 'Fantasy', 'Battle Royale', 'Card Game', 'Other'];
const PLATFORMS = ['Mobile', 'PC', 'Console', 'Cross-Platform'];
const STATUSES = [
  { value: 'live', label: '🟢 Live', color: '#00ff88' },
  { value: 'coming_soon', label: '🟡 Coming Soon', color: '#ffd700' },
  { value: 'maintenance', label: '🔴 Maintenance', color: '#ff6b6b' },
];

const EMPTY_FORM = {
  name: '', genre: 'Action', description: '',
  imageUrl: '', platform: 'Mobile', status: 'live', featured: false,
};

export default function GamesPage() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('list'); // 'list' | 'add'
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const load = async () => {
    try {
      const { data } = await getGames();
      setGames(data);
    } catch (err) {
      toast.error('Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.genre) return toast.error('Name and genre are required');
    setSaving(true);
    try {
      if (editId) {
        const { data } = await updateGame(editId, form);
        setGames(prev => prev.map(g => g._id === editId ? data : g));
        toast.success('Game updated!');
        setEditId(null);
      } else {
        const { data } = await createGame(form);
        setGames(prev => [data, ...prev]);
        toast.success('Game added!');
      }
      setForm(EMPTY_FORM);
      setTab('list');
    } catch (err) {
      toast.error('Failed to save game');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (game) => {
    setForm({
      name: game.name, genre: game.genre,
      description: game.description || '',
      imageUrl: game.imageUrl || '',
      platform: game.platform || 'Mobile',
      status: game.status || 'live',
      featured: game.featured || false,
    });
    setEditId(game._id);
    setTab('add');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this game?')) return;
    try {
      await deleteGame(id);
      setGames(prev => prev.filter(g => g._id !== id));
      toast.success('Game deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleToggleFeatured = async (game) => {
    try {
      const { data } = await updateGame(game._id, { ...game, featured: !game.featured });
      setGames(prev => prev.map(g => g._id === game._id ? data : g));
      toast.success(data.featured ? 'Marked as featured!' : 'Removed from featured');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const filtered = games.filter(g => {
    const matchSearch = g.name.toLowerCase().includes(search.toLowerCase()) ||
      g.genre.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || g.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const statusInfo = (s) => STATUSES.find(x => x.value === s) || STATUSES[0];

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🎮 Games</h1>
          <p style={styles.subtitle}>Manage your game catalogue</p>
        </div>
        <button
          style={styles.addBtn}
          onClick={() => { setForm(EMPTY_FORM); setEditId(null); setTab('add'); }}
        >
          + Add Game
        </button>
      </div>

      {/* Stats row */}
      <div style={styles.statsRow}>
        {[
          { label: 'Total Games', value: games.length, icon: '🎮' },
          { label: 'Live', value: games.filter(g => g.status === 'live').length, icon: '🟢' },
          { label: 'Coming Soon', value: games.filter(g => g.status === 'coming_soon').length, icon: '🟡' },
          { label: 'Featured', value: games.filter(g => g.featured).length, icon: '⭐' },
        ].map(s => (
          <div key={s.label} style={styles.statCard}>
            <span style={styles.statIcon}>{s.icon}</span>
            <div>
              <div style={styles.statValue}>{s.value}</div>
              <div style={styles.statLabel}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {['list', 'add'].map(t => (
          <button
            key={t}
            onClick={() => { setTab(t); if (t === 'list') { setForm(EMPTY_FORM); setEditId(null); } }}
            style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }}
          >
            {t === 'list' ? '📋 Game List' : editId ? '✏️ Edit Game' : '➕ Add New Game'}
          </button>
        ))}
      </div>

      {/* LIST TAB */}
      {tab === 'list' && (
        <div style={styles.listSection}>
          {/* Filters */}
          <div style={styles.filters}>
            <input
              style={styles.search}
              placeholder="🔍 Search games..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div style={styles.filterBtns}>
              {['all', 'live', 'coming_soon', 'maintenance'].map(s => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  style={{ ...styles.filterBtn, ...(filterStatus === s ? styles.filterBtnActive : {}) }}
                >
                  {s === 'all' ? 'All' : statusInfo(s).label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div style={styles.empty}>Loading games...</div>
          ) : filtered.length === 0 ? (
            <div style={styles.empty}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎮</div>
              <div>No games found. Add your first game!</div>
            </div>
          ) : (
            <div style={styles.grid}>
              {filtered.map(game => (
                <div key={game._id} style={styles.card}>
                  {/* Image */}
                  <div style={styles.cardImgWrap}>
                    {game.imageUrl ? (
                      <img src={game.imageUrl} alt={game.name} style={styles.cardImg} />
                    ) : (
                      <div style={styles.cardImgPlaceholder}>🎮</div>
                    )}
                    {game.featured && <span style={styles.featuredBadge}>⭐ Featured</span>}
                    <span style={{ ...styles.statusBadge, color: statusInfo(game.status).color, borderColor: statusInfo(game.status).color }}>
                      {statusInfo(game.status).label}
                    </span>
                  </div>

                  {/* Info */}
                  <div style={styles.cardBody}>
                    <div style={styles.cardTop}>
                      <h3 style={styles.cardName}>{game.name}</h3>
                      <span style={styles.genreBadge}>{game.genre}</span>
                    </div>
                    <p style={styles.cardDesc}>{game.description || 'No description added.'}</p>
                    <div style={styles.cardMeta}>
                      <span style={styles.metaTag}>📱 {game.platform}</span>
                    </div>
                    <div style={styles.cardActions}>
                      <button
                        style={{ ...styles.actionBtn, ...(game.featured ? styles.featuredBtn : {}) }}
                        onClick={() => handleToggleFeatured(game)}
                        title={game.featured ? 'Remove from featured' : 'Mark as featured'}
                      >
                        {game.featured ? '★ Featured' : '☆ Feature'}
                      </button>
                      <button style={styles.editBtn} onClick={() => handleEdit(game)}>✏️ Edit</button>
                      <button style={styles.deleteBtn} onClick={() => handleDelete(game._id)}>🗑</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ADD / EDIT TAB */}
      {tab === 'add' && (
        <div style={styles.formSection}>
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGrid}>
              {/* Name */}
              <div style={styles.field}>
                <label style={styles.label}>GAME NAME *</label>
                <input
                  style={styles.input}
                  placeholder="e.g. Dragon Quest Arena"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                />
              </div>

              {/* Genre */}
              <div style={styles.field}>
                <label style={styles.label}>GENRE *</label>
                <select style={styles.input} value={form.genre} onChange={e => setForm({ ...form, genre: e.target.value })}>
                  {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>

              {/* Platform */}
              <div style={styles.field}>
                <label style={styles.label}>PLATFORM</label>
                <select style={styles.input} value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })}>
                  {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              {/* Status */}
              <div style={styles.field}>
                <label style={styles.label}>STATUS</label>
                <select style={styles.input} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>

            {/* Image URL */}
            <div style={styles.field}>
              <label style={styles.label}>IMAGE URL</label>
              <input
                style={styles.input}
                placeholder="https://... (paste a direct image link)"
                value={form.imageUrl}
                onChange={e => setForm({ ...form, imageUrl: e.target.value })}
              />
              {form.imageUrl && (
                <img src={form.imageUrl} alt="preview" style={styles.imgPreview}
                  onError={e => { e.target.style.display = 'none'; }} />
              )}
            </div>

            {/* Description */}
            <div style={styles.field}>
              <label style={styles.label}>DESCRIPTION</label>
              <textarea
                style={{ ...styles.input, minHeight: '100px', resize: 'vertical' }}
                placeholder="Describe the game, gameplay, rewards..."
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
              />
            </div>

            {/* Featured toggle */}
            <div style={styles.toggleRow}>
              <span style={styles.toggleLabel}>⭐ Mark as Featured Game</span>
              <div
                style={{ ...styles.toggle, ...(form.featured ? styles.toggleOn : {}) }}
                onClick={() => setForm({ ...form, featured: !form.featured })}
              >
                <div style={{ ...styles.toggleThumb, ...(form.featured ? styles.toggleThumbOn : {}) }} />
              </div>
            </div>

            <div style={styles.formActions}>
              <button type="button" style={styles.cancelBtn}
                onClick={() => { setTab('list'); setForm(EMPTY_FORM); setEditId(null); }}>
                Cancel
              </button>
              <button type="submit" style={{ ...styles.submitBtn, opacity: saving ? 0.6 : 1 }} disabled={saving}>
                {saving ? 'Saving...' : editId ? '💾 Update Game' : '🎮 Add Game'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding: '28px', overflowY: 'auto', height: '100%', fontFamily: "'Exo 2', sans-serif", color: '#e2e8f0' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { margin: 0, fontSize: '26px', fontWeight: '700', fontFamily: "'Rajdhani', sans-serif", letterSpacing: '1px' },
  subtitle: { margin: '4px 0 0', color: 'rgba(255,255,255,0.3)', fontSize: '13px' },
  addBtn: {
    background: 'linear-gradient(90deg, #FFB300, #00aaff)', border: 'none',
    borderRadius: '10px', color: '#080c14', padding: '10px 20px',
    fontSize: '14px', fontWeight: '900', cursor: 'pointer',
    fontFamily: "'Rajdhani', sans-serif", letterSpacing: '1px',
  },
  statsRow: { display: 'flex', gap: '14px', marginBottom: '24px', flexWrap: 'wrap' },
  statCard: {
    display: 'flex', alignItems: 'center', gap: '12px',
    background: '#0d1420', border: '1px solid rgba(255,179,0,0.08)',
    borderRadius: '12px', padding: '14px 20px', flex: '1', minWidth: '130px',
  },
  statIcon: { fontSize: '24px' },
  statValue: { fontSize: '22px', fontWeight: '700', color: '#FFB300', fontFamily: "'Rajdhani', sans-serif" },
  statLabel: { fontSize: '11px', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' },
  tabs: { display: 'flex', gap: '8px', marginBottom: '20px' },
  tab: {
    padding: '9px 20px', borderRadius: '10px', border: '1px solid rgba(255,179,0,0.1)',
    background: 'transparent', color: 'rgba(255,255,255,0.4)',
    cursor: 'pointer', fontSize: '13px', fontWeight: '600',
    fontFamily: "'Exo 2', sans-serif",
  },
  tabActive: { background: 'rgba(255,179,0,0.08)', color: '#FFB300', borderColor: 'rgba(255,179,0,0.3)' },
  listSection: {},
  filters: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
  search: {
    flex: 1, minWidth: '200px', background: '#0d1420',
    border: '1px solid rgba(255,179,0,0.1)', borderRadius: '10px',
    padding: '10px 14px', color: '#fff', fontSize: '14px',
    fontFamily: "'Exo 2', sans-serif", outline: 'none',
  },
  filterBtns: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  filterBtn: {
    padding: '8px 14px', borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'transparent', color: 'rgba(255,255,255,0.4)',
    cursor: 'pointer', fontSize: '12px', fontFamily: "'Exo 2', sans-serif",
  },
  filterBtnActive: { background: 'rgba(255,179,0,0.08)', color: '#FFB300', borderColor: 'rgba(255,179,0,0.3)' },
  empty: { textAlign: 'center', color: 'rgba(255,255,255,0.25)', padding: '60px', fontSize: '15px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '18px' },
  card: { background: '#0d1420', borderRadius: '16px', border: '1px solid rgba(255,179,0,0.08)', overflow: 'hidden' },
  cardImgWrap: { position: 'relative' },
  cardImg: { width: '100%', height: '160px', objectFit: 'cover', display: 'block' },
  cardImgPlaceholder: {
    width: '100%', height: '130px', background: 'rgba(255,179,0,0.04)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px',
  },
  featuredBadge: {
    position: 'absolute', top: '10px', left: '10px',
    background: 'rgba(255,215,0,0.15)', border: '1px solid rgba(255,215,0,0.4)',
    color: '#ffd700', borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: '700',
  },
  statusBadge: {
    position: 'absolute', top: '10px', right: '10px',
    background: 'rgba(0,0,0,0.5)', border: '1px solid',
    borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: '700',
  },
  cardBody: { padding: '16px' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' },
  cardName: { margin: 0, fontSize: '16px', fontWeight: '700', color: '#e2e8f0', fontFamily: "'Rajdhani', sans-serif" },
  genreBadge: {
    background: 'rgba(0,200,255,0.1)', border: '1px solid rgba(0,200,255,0.2)',
    color: '#00aaff', borderRadius: '6px', padding: '2px 9px', fontSize: '11px', flexShrink: 0,
  },
  cardDesc: { color: 'rgba(255,255,255,0.3)', fontSize: '12px', lineHeight: '1.5', marginBottom: '10px', margin: '0 0 10px' },
  cardMeta: { display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' },
  metaTag: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '6px', padding: '3px 9px', fontSize: '11px', color: 'rgba(255,255,255,0.4)',
  },
  cardActions: { display: 'flex', gap: '8px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '12px' },
  actionBtn: {
    flex: 1, padding: '7px', borderRadius: '8px', border: '1px solid rgba(255,215,0,0.2)',
    background: 'rgba(255,215,0,0.06)', color: 'rgba(255,215,0,0.7)',
    cursor: 'pointer', fontSize: '12px', fontFamily: "'Exo 2', sans-serif",
  },
  featuredBtn: { background: 'rgba(255,215,0,0.15)', color: '#ffd700', borderColor: 'rgba(255,215,0,0.4)' },
  editBtn: {
    padding: '7px 12px', borderRadius: '8px',
    border: '1px solid rgba(255,179,0,0.2)', background: 'rgba(255,179,0,0.06)',
    color: '#FFB300', cursor: 'pointer', fontSize: '12px',
  },
  deleteBtn: {
    padding: '7px 12px', borderRadius: '8px',
    border: '1px solid rgba(255,50,50,0.2)', background: 'rgba(255,50,50,0.06)',
    color: '#ff6b6b', cursor: 'pointer', fontSize: '14px',
  },
  formSection: { background: '#0d1420', borderRadius: '16px', border: '1px solid rgba(255,179,0,0.08)', padding: '28px' },
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { color: 'rgba(255,179,0,0.6)', fontSize: '11px', fontWeight: '700', letterSpacing: '2px' },
  input: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,179,0,0.12)',
    borderRadius: '10px', padding: '11px 14px', color: '#fff',
    fontSize: '14px', fontFamily: "'Exo 2', sans-serif", outline: 'none',
  },
  imgPreview: { width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '10px', marginTop: '8px' },
  toggleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' },
  toggleLabel: { color: '#e2e8f0', fontSize: '14px' },
  toggle: { width: '46px', height: '26px', borderRadius: '13px', background: 'rgba(255,255,255,0.1)', position: 'relative', cursor: 'pointer', transition: 'background 0.2s' },
  toggleOn: { background: 'rgba(255,179,0,0.3)' },
  toggleThumb: { position: 'absolute', top: '3px', left: '3px', width: '20px', height: '20px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' },
  toggleThumbOn: { left: '23px', background: '#FFB300' },
  formActions: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  cancelBtn: { padding: '11px 24px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '14px', fontFamily: "'Exo 2', sans-serif" },
  submitBtn: { padding: '11px 28px', borderRadius: '10px', border: 'none', background: 'linear-gradient(90deg, #FFB300, #00aaff)', color: '#080c14', fontSize: '14px', fontWeight: '900', cursor: 'pointer', fontFamily: "'Rajdhani', sans-serif", letterSpacing: '1px' },
};
